/**
 * Form version families. Pure: no React, no data access. Unit tested in
 * src/test/formFamily.test.ts.
 *
 * Publishing a form draft inserts a NEW `forms` row per version, so one form a
 * person thinks of as "the Laptop request" is several rows over time. A family
 * is every version published from the same draft (`draftId`). A row with no
 * draft is a family of one. Two forms that merely share a name are NOT a
 * family: two unrelated forms called "Untitled form" must never merge.
 *
 * Everything that has to follow a form across a republish goes through here:
 *   - submissions lists (a request made on any version still shows up, read
 *     through the spec of the version it was made on),
 *   - workflow references to a form (trigger.formId, a Form step's formId),
 *     which keep the id of the version that was picked and are resolved to the
 *     latest live version when read, never rewritten,
 *   - the public link (slug + on/off), which is carried to the new version at
 *     publish time (see planPublicLinkCarryForward).
 */
import type { FormSubmission, PublishedForm } from '@/types/form-spec'

/** The least a row must carry to be placed in a family. */
export type FormFamilyMember = Pick<PublishedForm, 'id' | 'draftId' | 'version' | 'status'>

/** The family a form version belongs to: its source draft, else itself. */
export function formFamilyKey(form: Pick<PublishedForm, 'id' | 'draftId'>): string {
  return form.draftId ? `draft:${form.draftId}` : `form:${form.id}`
}

export interface FormFamilyIndex<T extends FormFamilyMember> {
  /** The exact version row, or null when it was not among the rows indexed. */
  get(formId: string | null | undefined): T | null
  /** The latest live (published) version of the family `formId` belongs to.
   *  Null when the id is unknown or its family has no live version. */
  latest(formId: string | null | undefined): T | null
  /** `latest(formId).id`, or the id unchanged when it cannot be resolved. */
  latestId(formId: string): string
  /** Every version id of the family, always including `formId` itself. */
  familyIds(formId: string): string[]
  /** True when both ids name the same form: equal ids, or versions of one family. */
  sameFamily(a: string | null | undefined, b: string | null | undefined): boolean
}

/**
 * Index a list of form versions by family. Pass every version you have (for
 * example ds.getForms({ allVersions: true })); an id that is not in the list
 * resolves to itself, so a caller holding only part of the picture degrades to
 * exact-id matching instead of guessing.
 */
export function buildFormFamilyIndex<T extends FormFamilyMember>(forms: readonly T[]): FormFamilyIndex<T> {
  const byId = new Map<string, T>()
  const members = new Map<string, T[]>()
  const latestLive = new Map<string, T>()
  for (const f of forms) {
    if (byId.has(f.id)) continue
    byId.set(f.id, f)
    const key = formFamilyKey(f)
    const list = members.get(key)
    if (list) list.push(f)
    else members.set(key, [f])
    if (f.status !== 'published') continue
    const cur = latestLive.get(key)
    if (!cur || f.version > cur.version) latestLive.set(key, f)
  }
  const keyOf = (id: string | null | undefined): string | null => {
    const f = id ? byId.get(id) : undefined
    return f ? formFamilyKey(f) : null
  }
  return {
    get: (id) => (id ? byId.get(id) ?? null : null),
    latest: (id) => {
      const key = keyOf(id)
      return key ? latestLive.get(key) ?? null : null
    },
    latestId: (id) => {
      const key = keyOf(id)
      return (key ? latestLive.get(key)?.id : undefined) ?? id
    },
    familyIds: (id) => {
      const key = keyOf(id)
      const ids = key ? (members.get(key) ?? []).map((f) => f.id) : []
      return ids.includes(id) ? ids : [id, ...ids]
    },
    sameFamily: (a, b) => {
      if (!a || !b) return false
      if (a === b) return true
      const ka = keyOf(a)
      return ka !== null && ka === keyOf(b)
    },
  }
}

/** The latest live version of each family, in first-seen order. */
export function latestLiveVersions<T extends FormFamilyMember>(forms: readonly T[]): T[] {
  const latest = new Map<string, T>()
  for (const f of forms) {
    if (f.status !== 'published') continue
    const key = formFamilyKey(f)
    const cur = latest.get(key)
    if (!cur || f.version > cur.version) latest.set(key, f)
  }
  return [...latest.values()]
}

/**
 * The form a submission is read through: the version it was made on, so a field
 * renamed or removed since still shows the value that was entered. When that
 * row is not available the latest live version of its family is used, then
 * `fallback` (the form a page is already showing). Null when none is known.
 */
export function formForSubmission<T extends FormFamilyMember>(
  submission: Pick<FormSubmission, 'formId'>,
  index: FormFamilyIndex<T>,
  fallback?: T | null,
): T | null {
  return index.get(submission.formId) ?? index.latest(submission.formId) ?? fallback ?? null
}

// ---------------------------------------------------------------------------
// Public link (slug + on/off) across versions
// ---------------------------------------------------------------------------

export type PublicLinkRow = Pick<PublishedForm, 'id' | 'version' | 'publicSlug' | 'isPublic'>

/** One write of the two public-link columns of one `forms` row. */
export interface PublicLinkWrite {
  formId: string
  publicSlug: string | null
  isPublic: boolean
}

export interface PublicLinkPlan {
  /** The older version that holds the link today. */
  fromFormId: string
  slug: string
  isPublic: boolean
  /** Apply in order. The slug is unique across rows (migration 00403), so the
   *  old row is cleared BEFORE the new row takes the slug: at no point do two
   *  rows hold it. */
  steps: [PublicLinkWrite, PublicLinkWrite]
  /** Apply when the second step fails after the first succeeded, so the link
   *  is put back on the version it came from instead of being lost. */
  rollback: PublicLinkWrite[]
}

/** The version that holds a family's link: a public one first, then the newest. */
function linkCarrier(rows: readonly PublicLinkRow[], excludeId: string): PublicLinkRow | null {
  let best: PublicLinkRow | null = null
  for (const r of rows) {
    if (r.id === excludeId || !r.publicSlug) continue
    if (!best
      || (r.isPublic && !best.isPublic)
      || (r.isPublic === best.isPublic && r.version > best.version)) best = r
  }
  return best
}

/**
 * Plan moving a family's public link onto `targetFormId` (the version just
 * published, or the latest version when a link is being turned back on).
 * `family` is every version of the same draft. Returns null when no other
 * version holds a slug: there is nothing to carry.
 *
 * `forcePublic` turns the link on as it moves (re-enabling a link that was
 * switched off); otherwise the on/off state is carried as it stands, so a link
 * that was off stays off but keeps its address.
 */
export function planPublicLinkCarryForward(
  family: readonly PublicLinkRow[],
  targetFormId: string,
  opts?: { forcePublic?: boolean },
): PublicLinkPlan | null {
  const carrier = linkCarrier(family, targetFormId)
  if (!carrier || !carrier.publicSlug) return null
  const slug = carrier.publicSlug
  const isPublic = opts?.forcePublic ? true : carrier.isPublic
  return {
    fromFormId: carrier.id,
    slug,
    isPublic,
    steps: [
      { formId: carrier.id, publicSlug: null, isPublic: false },
      { formId: targetFormId, publicSlug: slug, isPublic },
    ],
    rollback: [{ formId: carrier.id, publicSlug: slug, isPublic: carrier.isPublic }],
  }
}

/**
 * The link a form list should show for the latest version of a family. A link
 * published before links were carried forward can still sit on an older row;
 * the public page resolves it to the latest version, so the list must show it
 * too, or nobody could find it to turn it off.
 */
export function withFamilyPublicLink<T extends PublicLinkRow>(latest: T, family: readonly PublicLinkRow[]): T {
  if (latest.isPublic && latest.publicSlug) return latest
  let live: PublicLinkRow | null = null
  for (const r of family) {
    if (r.id === latest.id || !r.isPublic || !r.publicSlug) continue
    if (!live || r.version > live.version) live = r
  }
  return live ? { ...latest, publicSlug: live.publicSlug, isPublic: true } : latest
}

/**
 * A new version was published, but its public link could not be moved to it.
 * The publish itself stands: `form` is the new live version.
 */
export class FormPublicLinkError extends Error {
  readonly form: PublishedForm
  /** True when the link could not be put back on the older version either. */
  readonly linkLost: boolean
  constructor(message: string, form: PublishedForm, linkLost: boolean) {
    super(message)
    this.name = 'FormPublicLinkError'
    this.form = form
    this.linkLost = linkLost
  }
}
